import type { ReactNode } from "react";

type Tone = "neutral" | "info" | "success" | "warning" | "danger";

/**
 * Pastille d'état en ligne (`<span>` arrondi). Porte un statut court (« Prêt »,
 * « En cours », « 2 à corriger »…). La couleur n'est jamais seule : le libellé
 * porte toujours le sens (§5.2). La norme reste douce ; l'exception qui appelle
 * une action (warning/danger) passe en pastille pleine pour ressortir.
 *
 * Quand l'utiliser : signaler l'état d'un item dans une liste / un en-tête.
 * Pour une action cliquable, utiliser `Button`, pas un badge.
 */
interface BadgeProps {
  /**
   * Ton sémantique (défaut `neutral`).
   * - `neutral` : gris doux (info de fond).
   * - `info` : mauve doux (en cours / neutre actif).
   * - `success` : émeraude doux (prêt / succès).
   * - `warning` : ambre plein (attention — demande un regard).
   * - `danger` : rouge plein + blanc (échec / erreur).
   */
  tone?: Tone;
  /** Contenu (texte court ; une icône en tête est OK pour renforcer le sens). */
  children: ReactNode;
  /** Classes utilitaires additionnelles (fusionnées en fin de liste). */
  className?: string;
}

// On met en valeur l'exception : « Prêt »/info/neutre restent en teinte douce
// (la norme reste calme), les états qui demandent une action (à reprendre,
// échec/erreur) passent en pastille PLEINE pour ressortir.
// Tons alignés sur les paires AA du DS (§5.2). La norme (neutral/info/success)
// reste en teinte douce ; l'exception qui demande une action (warning/danger)
// passe en pastille pleine pour ressortir. La couleur n'est jamais seule : le
// libellé porte toujours le sens.
const TONE: Record<Tone, string> = {
  neutral: "bg-ground text-muted",
  info: "bg-flambeau-100 text-flambeau-700",
  success: "bg-emerald-soft text-emerald-deep",
  warning: "bg-amber-pill text-amber-deep",
  danger: "bg-red text-white",
};

export function Badge({ tone = "neutral", className = "", children }: BadgeProps) {
  const classes = [
    "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-label font-semibold",
    TONE[tone],
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return <span className={classes}>{children}</span>;
}
